/**
 * One reading, in a window of its own.
 *
 * The same cell had been written three times — the migration facts on Капитал,
 * the machine readings on Система, the funnel stages on Рейтинг — each with
 * slightly different padding and a slightly different grey. It is one component
 * now, which is also the only way the three screens can look like one product.
 *
 * A ground rather than an outline. An earlier pass argued the opposite and it
 * was half right: five outlined cards inside an outlined panel is a border for
 * every reading plus a border for the set. But space alone did not make the
 * figures findable — the owner read the result as "just rows" twice — and a
 * quiet ground separates a window from its panel without adding a rule for each
 * one. The hairline is there for the light theme, where a surface a shade off
 * white needs an edge to exist at all.
 */

type Props = {
  readonly label: string;
  /** Already formatted by the presentation primitive for its figure. */
  readonly value: React.ReactNode;
  /** The line under the figure: a denominator, a comparison, a qualifier. */
  readonly note?: React.ReactNode;
  /** The whole cell's explanation, on hover. */
  readonly title?: string | undefined;
  readonly testId?: string | undefined;
  readonly className?: string;
};

export function Stat({
  label,
  value,
  note,
  title,
  testId,
  className = "",
}: Props): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      title={title}
      className={`flex min-w-0 flex-col gap-0.5 rounded-row border border-border-subtle bg-surface-hover px-3 py-2 ${className}`.trim()}
    >
      {/* Wraps rather than truncates. "Взято в рассмотрен…" is not a shorter
          label, it is a different one, and the reader has no way to tell which
          word went missing. */}
      <span className="text-helper-sm text-balance text-text-muted">{label}</span>
      <span className="font-numeric text-body-main tabular-nums text-text-primary">
        {value}
      </span>
      {note !== undefined && (
        <span className="text-helper-sm text-text-muted">{note}</span>
      )}
    </div>
  );
}
